import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  authHeaders,
  automateRequestPath,
  getAutomateApiUrl,
  isAutomateRequestId,
  LEGACY_AUTOMATE_ID_MESSAGE,
} from '../automate/apiBase';

function rejectLegacyAutomateId(requestId, rejectWithValue) {
  if (!isAutomateRequestId(requestId)) {
    return rejectWithValue(LEGACY_AUTOMATE_ID_MESSAGE);
  }
  return null;
}

function normalizeInputsResponse(data) {
  const items = data.items || data.inputs || [];
  return { ...data, items, inputs: items };
}

function requestApiBase(_requestId) {
  return getAutomateApiUrl();
}

// What went wrong, in words a user can act on: a proxy timeout or an unreachable server
// says so, and the API's own message (`message`, `error` or FastAPI's `detail`) is shown.
function describeRequestError(error, fallback) {
  const status = error.response?.status;
  if ([502, 503, 504, 524].includes(status)) {
    return 'The server took too long to answer. Your files may still be processing — refresh the page in a minute before trying again.';
  }
  if (status === 413) return 'The files are too large to upload.';
  if (!error.response) return 'Could not reach the server. Check your connection and try again.';
  const data = error.response.data;
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  return data?.message || data?.error || detail || fallback;
}

function rejectApiError(error, fallback, rejectWithValue) {
  const data = error.response?.data;
  const message = data?.error || data?.message || fallback;
  const payload = { message };
  if (Array.isArray(data?.validationErrors) && data.validationErrors.length) {
    payload.validationErrors = data.validationErrors;
  }
  if (data?.validationReview && typeof data.validationReview === 'object') {
    payload.validationReview = data.validationReview;
  }
  if (payload.validationErrors || payload.validationReview) {
    return rejectWithValue(payload);
  }
  return rejectWithValue(message);
}

// Async thunk for fetching all requests with pagination and filters
export const fetchRequests = createAsyncThunk(
  'requests/fetchRequests',
  async ({ page = 1, limit = 10, status = null, featureType = null, projectId = null, countedOnly = false, includeInFlight = false } = {}, { rejectWithValue }) => {
    try {
      const base = getAutomateApiUrl(); // PyBE
      let url = `${base}/api/requests?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;
      if (featureType) url += `&featureType=${featureType}`;
      if (projectId) url += `&projectId=${projectId}`;
      if (countedOnly) url += `&countedOnly=true`;
      if (includeInFlight) url += `&includeInFlight=true`;

      const response = await axios.get(url, {
        headers: authHeaders(),
      });

      if (response.data.success) {
        return {
          requests: response.data.requests || [],
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 0,
          currentPage: response.data.currentPage || 1,
          totalTokens: response.data.totalTokens || 0,
          totalCost: response.data.totalCost || 0,
        };
      }

      return {
        requests: [],
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        totalTokens: 0,
        totalCost: 0,
      };
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch requests';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for fetching a single request by ID
export const fetchRequestById = createAsyncThunk(
  'requests/fetchRequestById',
  async (requestId, { rejectWithValue }) => {
    try {
      const base = requestApiBase(requestId);
      const response = await axios.get(`${base}/api/requests/${requestId}`, {
        headers: authHeaders(),
      });

      if (response.data.success && response.data.request) {
        return response.data.request;
      }

      return rejectWithValue('Request not found');
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue('Request not found');
      }
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to fetch request';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for creating a new request
export const createRequest = createAsyncThunk(
  'requests/createRequest',
  async ({
    featureType,
    requestData,
    projectId,
    file = null,
    filesByField = null,
    startNormalize = undefined,
  }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('featureType', featureType);
      const payload = { ...requestData };
      if (startNormalize !== undefined) {
        payload.startNormalize = !!startNormalize;
      } else if (featureType === 'requirement-intake') {
        payload.startNormalize = false;
      }
      formData.append('requestData', JSON.stringify(payload));
      let normalizedProjectId = Number.parseInt(String(projectId ?? '').trim(), 10);
      if (!Number.isInteger(normalizedProjectId) || normalizedProjectId < 1) {
        return rejectWithValue('Please select a valid project before generating documents.');
      }
      formData.append('projectId', String(normalizedProjectId));
      
      if (file) {
        formData.append('file', file);
      }
      if (filesByField && typeof filesByField === 'object') {
        (filesByField.documents || []).forEach((f) => formData.append('documents', f));
        (filesByField.codeFiles || []).forEach((f) => formData.append('codeFiles', f));
        (filesByField.images || []).forEach((f) => formData.append('images', f));
      }

      const base = getAutomateApiUrl();
      const response = await axios.post(`${base}/api/requests`, formData, {
        headers: authHeaders(),
      });

      if (response.data.success) {
        return response.data;
      }

      return rejectWithValue('Failed to create request');
    } catch (error) {
      return rejectWithValue(describeRequestError(error, 'Failed to create request'));
    }
  }
);

// Confirm Requirement Intake on PyBE.
export const confirmRequest = createAsyncThunk(
  'requests/confirmRequest',
  async ({ requestId, spec = null, proceedWithWarnings = false }, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const body = { proceedWithWarnings: !!proceedWithWarnings };
      if (spec && typeof spec === 'object') body.spec = spec;
      const response = await axios.post(automateRequestPath(requestId, '/confirm'), body, {
        headers: authHeaders(),
      });
      if (response.data.success) {
        return response.data;
      }
      return rejectWithValue('Failed to confirm request');
    } catch (error) {
      return rejectApiError(error, 'Failed to confirm request', rejectWithValue);
    }
  }
);

/** Automate Step 2: save draft spec edits (PATCH .../spec). */
export const patchRequestSpec = createAsyncThunk(
  'requests/patchRequestSpec',
  async ({ requestId, spec, activeFeatureId, activeRequirementId }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${getAutomateApiUrl()}/api/requests/${requestId}/spec`, {
        spec,
        activeFeatureId,
        activeRequirementId,
      }, {
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to save spec');
    } catch (error) {
      return rejectApiError(error, 'Failed to save spec', rejectWithValue);
    }
  }
);

/** Automate Step 2: run blocker resolution agent. */
export const resolveBlockers = createAsyncThunk(
  'requests/resolveBlockers',
  async ({ requestId, spec = null }, { rejectWithValue }) => {
    try {
      const body = spec && typeof spec === 'object' ? { spec } : {};
      const response = await axios.post(`${getAutomateApiUrl()}/api/requests/${requestId}/resolve-blockers`, body, {
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to resolve blockers');
    } catch (error) {
      return rejectApiError(error, 'Failed to resolve blockers', rejectWithValue);
    }
  }
);

/** Automate Step 2: ignore/waive a blocking validation issue. */
export const waiveBlocker = createAsyncThunk(
  'requests/waiveBlocker',
  async ({ requestId, path, message, reason = '' }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${getAutomateApiUrl()}/api/requests/${requestId}/waive-blocker`, {
        path,
        message,
        reason,
      }, {
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to ignore blocker');
    } catch (error) {
      return rejectApiError(error, 'Failed to ignore blocker', rejectWithValue);
    }
  }
);

/** Automate Step 3: kick off Step 4 test-case generation. */
export const generateTestSuite = createAsyncThunk(
  'requests/generateTestSuite',
  async ({ requestId, config, regenerate = false }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${getAutomateApiUrl()}/api/requests/${requestId}/generate`, { config, regenerate }, {
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to start generation');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to start generation';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Automate Step 4: persist suite edits. */
export const saveTestSuite = createAsyncThunk(
  'requests/saveTestSuite',
  async ({ requestId, testCases }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${getAutomateApiUrl()}/api/requests/${requestId}/suite`, { testCases }, {
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to save test suite');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to save test suite';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Automate Step 4: resolve invented test data via HIL → spec patch. */
export const resolveSuiteGrounding = createAsyncThunk(
  'requests/resolveSuiteGrounding',
  async ({ requestId, itemId, choice, answer, symbolicToken }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${getAutomateApiUrl()}/api/requests/${requestId}/suite-grounding/resolve`,
        { itemId, choice, answer, symbolicToken },
        { headers: authHeaders() },
      );
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to resolve suite grounding');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to resolve suite grounding';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Automate Step 4: QA suite linter, optional autoFix. */
export const lintTestSuite = createAsyncThunk(
  'requests/lintTestSuite',
  async ({ requestId, autoFix = false }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${getAutomateApiUrl()}/api/requests/${requestId}/lint-suite`, { autoFix }, {
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to lint test suite');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to lint test suite';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Ingest step: resume the latest Automate request from the DB (survives closing the tab). */
export const fetchActiveAutomateRequest = createAsyncThunk(
  'requests/fetchActiveAutomateRequest',
  async ({ projectId, excludeIds = [] } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (projectId != null && String(projectId).trim() !== '') params.projectId = projectId;
      if (excludeIds.length) params.excludeIds = excludeIds.join(',');
      const response = await axios.get(`${getAutomateApiUrl()}/api/requests/automate/active`, {
        params,
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to resolve active request');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to resolve active request';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Run Docling + code narrative enrichment after archive (Step 1 upload). */
export const enrichRequestSources = createAsyncThunk(
  'requests/enrichRequestSources',
  async (requestId, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const url = automateRequestPath(requestId, '/enrich-sources');
      const response = await axios.post(url, {}, { headers: authHeaders() });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to generate Markdown sources');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.response?.data?.message || 'Failed to generate Markdown sources';
      return rejectWithValue(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  }
);

/** Re-run Docling / code narrative / UI vision for a single archived input. */
export const reparseInputMarkdown = createAsyncThunk(
  'requests/reparseInputMarkdown',
  async ({ requestId, inputId }, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const url = automateRequestPath(requestId, `/inputs/${inputId}/reparse-markdown`);
      const response = await axios.post(url, {}, { headers: authHeaders() });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to reparse Markdown');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.response?.data?.message || 'Failed to reparse Markdown';
      return rejectWithValue(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  }
);

/** Fetch full archived Markdown for a document input. */
export const fetchInputMarkdown = createAsyncThunk(
  'requests/fetchInputMarkdown',
  async ({ requestId, inputId }, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const url = automateRequestPath(requestId, `/inputs/${inputId}/markdown`);
      const response = await axios.get(url, { headers: authHeaders() });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to load markdown');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.response?.data?.message || 'Failed to load markdown';
      return rejectWithValue(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  }
);

/** Ingest step: list already-archived sources for a request. */
/** Spec editor: passages of the request's uploaded inputs that name a field or button. */
export const searchRequestInputs = createAsyncThunk(
  'requests/searchRequestInputs',
  async ({ requestId, query }, { rejectWithValue }) => {
    try {
      const url = automateRequestPath(requestId, '/inputs-search');
      const response = await axios.get(url, { headers: authHeaders(), params: { q: query } });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to search the inputs');
    } catch (error) {
      return rejectWithValue(describeRequestError(error, 'Failed to search the inputs'));
    }
  }
);

export const fetchRequestInputs = createAsyncThunk(
  'requests/fetchRequestInputs',
  async (requestId, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const url = automateRequestPath(requestId, '/inputs');
      const response = await axios.get(url, {
        headers: authHeaders(),
      });
      if (response.data.success) return normalizeInputsResponse(response.data);
      return rejectWithValue('Failed to load sources');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to load sources';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Ingest step: add sources to an existing draft request whenever needed. */
export const addRequestInputs = createAsyncThunk(
  'requests/addRequestInputs',
  async ({ requestId, intakeItems, filesByField = null }, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const url = automateRequestPath(requestId, '/inputs');
      const formData = new FormData();
      formData.append('intakeItems', JSON.stringify(intakeItems));
      if (filesByField && typeof filesByField === 'object') {
        (filesByField.documents || []).forEach((f) => formData.append('documents', f));
        (filesByField.codeFiles || []).forEach((f) => formData.append('codeFiles', f));
        (filesByField.images || []).forEach((f) => formData.append('images', f));
      }
      const response = await axios.post(url, formData, {
        headers: authHeaders(),
      });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to add sources');
    } catch (error) {
      return rejectWithValue(describeRequestError(error, 'Failed to add sources'));
    }
  }
);

/** After editing raw inputs, discard the cached spec and re-run normalize. */
export const renormalizeRequest = createAsyncThunk(
  'requests/renormalizeRequest',
  async ({ requestId, mode = 'full', inputIds = [] }, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const url = automateRequestPath(requestId, '/renormalize');
      const response = await axios.post(
        url,
        { mode, inputIds },
        { headers: authHeaders() },
      );
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to re-normalize request');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || 'Failed to re-normalize request';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Continue normalization after reviewing discovered modules. */
export const updateModuleInventoryRequest = createAsyncThunk(
  'requests/updateModuleInventoryRequest',
  async ({ requestId, modules }, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      const url = automateRequestPath(requestId, '/module-inventory');
      const response = await axios.patch(url, { modules }, { headers: authHeaders() });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to save module inventory');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || 'Failed to save module inventory';
      return rejectWithValue(errorMsg);
    }
  }
);

/** Continue normalization after reviewing discovered modules. */
export const confirmModulesRequest = createAsyncThunk(
  'requests/confirmModulesRequest',
  async ({ requestId, modules }, { rejectWithValue }) => {
    try {
      const legacy = rejectLegacyAutomateId(requestId, rejectWithValue);
      if (legacy) return legacy;
      if (Array.isArray(modules) && modules.length) {
        const saveUrl = automateRequestPath(requestId, '/module-inventory');
        const saveResponse = await axios.patch(saveUrl, { modules }, { headers: authHeaders() });
        if (!saveResponse.data.success) {
          return rejectWithValue('Failed to save module inventory');
        }
      }
      const url = automateRequestPath(requestId, '/confirm-modules');
      const response = await axios.post(url, {}, { headers: authHeaders() });
      if (response.data.success) return response.data;
      return rejectWithValue('Failed to confirm modules');
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.response?.data?.detail || 'Failed to confirm modules';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for deleting a request
export const deleteRequest = createAsyncThunk(
  'requests/deleteRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      const base = requestApiBase(requestId);
      const response = await axios.delete(`${base}/api/requests/${requestId}`, {
        headers: authHeaders(),
      });

      if (response.data.success) {
        return requestId;
      }

      return rejectWithValue('Failed to delete request');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to delete request';
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for resubmitting a request
export const resubmitRequest = createAsyncThunk(
  'requests/resubmitRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      const base = requestApiBase(requestId);
      const response = await axios.post(`${base}/api/requests/${requestId}/resubmit`, {}, {
        headers: authHeaders(),
      });

      if (response.data.success) {
        return response.data;
      }

      return rejectWithValue('Failed to resubmit request');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to resubmit request';
      return rejectWithValue(errorMsg);
    }
  }
);

const initialState = {
  // All requests list
  allRequests: [],
  totalCount: 0,
  totalPages: 0,
  currentPage: 1,
  
  // Single request details
  currentRequest: null,
  
  // User statistics
  totalTokens: 0,
  totalCost: 0,
  
  // UI states
  loading: false,
  error: null,
  success: false,
  
  // Download states
  downloading: false,
  downloadError: null,
  downloadSuccess: false,
  lastDownloadedFileName: null,
  
  // Last created request info
  lastCreatedRequestId: null,
  lastCreatedStatus: null,
};

const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.lastCreatedRequestId = null;
      state.lastCreatedStatus = null;
    },
    clearCurrentRequest: (state) => {
      state.currentRequest = null;
    },
    setCurrentRequest: (state, action) => {
      state.currentRequest = action.payload;
    },
    clearDownloadError: (state) => {
      state.downloadError = null;
    },
    clearDownloadSuccess: (state) => {
      state.downloadSuccess = false;
      state.lastDownloadedFileName = null;
    },
    resetRequestsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch requests
      .addCase(fetchRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.allRequests = action.payload.requests;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.totalTokens = action.payload.totalTokens;
        state.totalCost = action.payload.totalCost;
        state.error = null;
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch request by ID
      .addCase(fetchRequestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequestById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload;
        state.error = null;
      })
      .addCase(fetchRequestById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentRequest = null;
      })
      // Create request
      .addCase(createRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastCreatedRequestId = action.payload.requestId;
        state.lastCreatedStatus = action.payload.status;
        state.error = null;
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      .addCase(confirmRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        if (state.currentRequest && action.payload.requestId === state.currentRequest.id) {
          state.currentRequest = {
            ...state.currentRequest,
            status: action.payload.status,
          };
        }
        state.allRequests = (state.allRequests || []).map((req) =>
          req.id === action.payload.requestId ? { ...req, status: action.payload.status } : req
        );
      })
      .addCase(confirmRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete request
      .addCase(deleteRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.allRequests = state.allRequests.filter(req => req.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deleteRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resubmit request
      .addCase(resubmitRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(resubmitRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.lastCreatedRequestId = action.payload.requestId;
        state.lastCreatedStatus = action.payload.status;
        state.error = null;
      })
      .addCase(resubmitRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearCurrentRequest,
  setCurrentRequest,
  clearDownloadError,
  clearDownloadSuccess,
  resetRequestsState,
} = requestsSlice.actions;

export default requestsSlice.reducer;
